// Stok Sayım modülü — sistem mevcudu vs sayılan miktar.
// Onayla → otomatik Sayım Fazlası + Sayım Eksiği fişleri üretir.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, ListPageHeader } from '../components/ui';
import { CardHero, CardStatusBadge } from '../components/card';
import DataTable from '../components/DataTable';
import SearchableSelect from '../components/SearchableSelect';
import { fmtAmount } from '../utils/numberFormat';

const fmt = (n) => fmtAmount(n);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '';
const today = () => new Date().toISOString().slice(0, 10);

const COUNT_TYPES = [
  { value: 'full',    label: 'Tam Sayım' },
  { value: 'partial', label: 'Kısmi Sayım' },
  { value: 'spot',    label: 'Spot Sayım' },
];

const STATUS_META = {
  draft:     { label: 'Taslak',   color: '#94a3b8' },
  counting:  { label: 'Sayımda',  color: '#3b82f6' },
  review:    { label: 'Mutabakat',color: '#f59e0b' },
  adjusted:  { label: 'Onaylandı',color: '#16a34a' },
  cancelled: { label: 'İptal',    color: '#94a3b8' },
};

export default function StockCountsPage({ onNavigate, autoOpenId } = {}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [lookups, setLookups] = useState({ warehouses: [], categories: [], brands: [], stocks: [], personnel: [], specialCodes1: [], specialCodes2: [] });

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('add'); // 'add' | 'view' | 'count'
  const [selected, setSelected] = useState(null);
  const [scope, setScope] = useState({ count_date: today(), count_type: 'partial', warehouse_id: '', category_id: '', brand_id: '', description: '', personnel_id: '', special_code1: '', special_code2: '' });
  const [tab, setTab] = useState('info'); // 'info' | 'count' | 'review'
  const [lines, setLines] = useState([]);  // [{ id, stock_id, stock_code, stock_name, expected_qty, counted_qty, variance, reason_code, line_notes }]
  const [saving, setSaving] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/stock-counts', { params: { search } });
      if (r.data.success) setList(r.data.data.items);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [search]);

  const fetchLookups = useCallback(async () => {
    try {
      const r = await api.get('/stock-counts/lookups/all');
      if (r.data.success) setLookups(r.data.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchList(); fetchLookups(); }, [fetchList, fetchLookups]);
  useEffect(() => { if (autoOpenId) openView(autoOpenId); }, [autoOpenId]); // eslint-disable-line

  const openAdd = () => {
    setMode('add'); setSelected(null);
    setScope({ count_date: today(), count_type: 'partial', warehouse_id: '', category_id: '', brand_id: '', description: '', personnel_id: '', special_code1: '', special_code2: '' });
    setLines([]); setTab('info'); setMsg(null);
    setShowModal(true);
  };

  const openView = async (id) => {
    try {
      const r = await api.get('/stock-counts/' + id);
      if (r.data.success) {
        const d = r.data.data;
        setSelected(d);
        setScope({
          count_date: d.count_date ? d.count_date.slice(0,10) : today(),
          count_type: d.count_type || 'partial',
          warehouse_id: d.warehouse_id || '',
          category_id: d.category_id || '',
          brand_id: d.brand_id || '',
          description: d.description || '',
          personnel_id: d.personnel_id || '',
          special_code1: d.special_code1 || '',
          special_code2: d.special_code2 || '',
        });
        setLines((d.lines || []).map(l => ({ ...l })));
        setMode(d.status === 'adjusted' || d.status === 'cancelled' ? 'view' : 'count');
        setTab(d.status === 'adjusted' ? 'review' : 'count');
        setMsg(null);
        setShowModal(true);
      }
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.message || 'Yüklenemedi' }); }
  };

  // Snapshot olarak sayımı oluştur (kapsam belirlenmiş)
  const createCount = async () => {
    setMsg(null);
    if (!scope.count_date) return setMsg({ type: 'error', text: 'Tarih zorunlu' });

    setSaving(true);
    try {
      const r = await api.post('/stock-counts', {
        count_date: scope.count_date,
        count_type: scope.count_type,
        warehouse_id: scope.warehouse_id || null,
        category_id: scope.category_id || null,
        brand_id: scope.brand_id || null,
        description: scope.description || null,
        personnel_id: scope.personnel_id || null,
        special_code1: scope.special_code1 || null,
        special_code2: scope.special_code2 || null,
      });
      if (r.data.success) {
        await fetchList();
        await openView(r.data.data.id);
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || e.message });
    } finally { setSaving(false); }
  };

  const updateLineQty = (lineId, key, val) => {
    setLines(prev => prev.map(l => {
      if (l.id !== lineId) return l;
      const updated = { ...l, [key]: val };
      if (key === 'counted_qty') {
        updated.variance = (parseFloat(val) || 0) - (parseFloat(l.expected_qty) || 0);
      }
      return updated;
    }));
  };

  const saveLines = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put('/stock-counts/' + selected.id, {
        description: scope.description || null,
        notes: selected.notes || null,
        personnel_id: scope.personnel_id || null,
        special_code1: scope.special_code1 || null,
        special_code2: scope.special_code2 || null,
        lines: lines.map(l => ({
          id: l.id,
          counted_qty: parseFloat(l.counted_qty) || 0,
          reason_code: l.reason_code || null,
          line_notes: l.line_notes || null,
        })),
      });
      setMsg({ type: 'success', text: 'Sayım kaydedildi' });
      setTimeout(() => setMsg(null), 1500);
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || e.message });
    } finally { setSaving(false); }
  };

  const adjust = async () => {
    if (!selected) return;
    const varianceLines = lines.filter(l => parseFloat(l.variance || 0) !== 0);
    if (varianceLines.length === 0) {
      if (!window.confirm('Fark olan satır yok. Yine de sayımı onaylamak ister misin?')) return;
    } else {
      const surplusCount = varianceLines.filter(l => parseFloat(l.variance) > 0).length;
      const deficitCount = varianceLines.filter(l => parseFloat(l.variance) < 0).length;
      const surplusValue = varianceLines.filter(l => parseFloat(l.variance) > 0)
        .reduce((s,l) => s + parseFloat(l.variance) * (parseFloat(l.unit_cost) || 0), 0);
      const deficitValue = varianceLines.filter(l => parseFloat(l.variance) < 0)
        .reduce((s,l) => s + Math.abs(parseFloat(l.variance)) * (parseFloat(l.unit_cost) || 0), 0);
      const netVal = surplusValue - deficitValue;
      const fmtTr = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
      if (!window.confirm(
        `Onayla & Düzelt:\n\n` +
        `+${surplusCount} satır fazlalık (+${fmtTr(surplusValue)} ₺) → Sayım Fazlası Fişi oluşacak\n` +
        `−${deficitCount} satır eksiklik (−${fmtTr(deficitValue)} ₺) → Sayım Eksiği Fişi oluşacak\n\n` +
        `Net Etki: ${netVal >= 0 ? '+' : ''}${fmtTr(netVal)} ₺\n\n` +
        `Devam edilsin mi?`
      )) return;
    }
    setAdjusting(true);
    try {
      // Önce satırları kaydet
      await api.put('/stock-counts/' + selected.id, {
        description: scope.description || null,
        notes: selected.notes || null,
        lines: lines.map(l => ({
          id: l.id, counted_qty: parseFloat(l.counted_qty) || 0,
          reason_code: l.reason_code || null, line_notes: l.line_notes || null,
        })),
      });
      // Sonra onayla
      const r = await api.post('/stock-counts/' + selected.id + '/adjust');
      if (r.data.success) {
        setMsg({ type: 'success', text: `Sayım onaylandı! Fazla: ${r.data.data.surplus_lines}, Eksik: ${r.data.data.deficit_lines}` });
        await fetchList();
        setTimeout(async () => { await openView(selected.id); }, 600);
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || e.message });
    } finally { setAdjusting(false); }
  };

  const remove = async () => {
    if (!selected) return;
    if (!window.confirm('Bu sayım silinecek. Devam edilsin mi?')) return;
    try {
      await api.delete('/stock-counts/' + selected.id);
      setShowModal(false);
      fetchList();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.message || e.message });
    }
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Stok Sayım"
        count={list.length} unit="sayım"
        search={search} onSearch={setSearch}
        searchPlaceholder="Sayım no, depo, açıklama ara..."
        onRefresh={fetchList} refreshing={loading}
        actions={<Button size="sm" onClick={openAdd}>+ Yeni Sayım</Button>}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="stock-counts-list"
          tableClassName="stocks-table" rowClassName="stocks-table-row"
          loading={loading} data={list} emptyText="Sayım bulunamadı" rowKey="id"
          onRowClick={r => openView(r.id)}
          columns={[
            { id: 'count_no', header: 'Sayım No', accessor: 'count_no', sortable: true, width: 160,
              cell: r => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.count_no}</span> },
            { id: 'count_date', header: 'Tarih', accessor: 'count_date', sortable: true, width: 100,
              cell: r => fmtDate(r.count_date) },
            { id: 'warehouse', header: 'Depo', accessor: 'warehouse_name', sortable: true, width: 140,
              cell: r => r.warehouse_name || <span style={{ color: 'var(--text-tertiary)' }}>Tüm depolar</span> },
            { id: 'scope', header: 'Kapsam', accessor: r => r.category_name || r.brand_name || '', width: 150,
              cell: r => {
                const parts = [];
                if (r.category_name) parts.push('Kat: ' + r.category_name);
                if (r.brand_name) parts.push('Marka: ' + r.brand_name);
                return parts.length ? <span style={{ fontSize: '0.78rem' }}>{parts.join(' · ')}</span> : <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
              } },
            { id: 'description', header: 'Açıklama', accessor: 'description', sortable: true,
              cell: r => r.description || '-' },
            { id: 'line_count', header: 'Satır', accessor: 'line_count', sortable: true, width: 70,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => r.line_count || 0 },
            { id: 'variance', header: 'Fark', accessor: 'variance_lines', sortable: true, width: 80,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => parseInt(r.variance_lines) > 0
                ? <span style={{ color: 'var(--warning)', fontWeight: 600 }}>{r.variance_lines}</span>
                : <span style={{ color: 'var(--success)' }}>0</span> },
            { id: 'status', header: 'Durum', accessor: 'status', sortable: true, width: 110,
              cell: r => {
                const s = STATUS_META[r.status] || { label: r.status, color: '#94a3b8' };
                return <span style={{ color: s.color, fontWeight: 600, fontSize: '0.78rem' }}>● {s.label}</span>;
              } },
          ]}
        />
      </div>

      {showModal && (
        <Modal
          icon={null}
          title={mode === 'add' ? 'Yeni Stok Sayım' : `Stok Sayım — ${selected?.count_no}`}
          size="xl"
          onClose={() => setShowModal(false)}
          closeOnOverlay={false}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {mode !== 'add' && selected && selected.status !== 'adjusted' && (
                  <Button variant="danger" onClick={remove}>🗑 Sil</Button>
                )}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
                {mode === 'add' && (
                  <>
                    <Button variant="ghost" onClick={() => setShowModal(false)}>Vazgeç</Button>
                    <Button variant="primary" onClick={createCount} loading={saving}>📋 Sayımı Başlat</Button>
                  </>
                )}
                {mode === 'count' && (
                  <>
                    <Button variant="ghost" onClick={() => setShowModal(false)}>Kapat</Button>
                    <Button variant="ghost" onClick={saveLines} loading={saving}>💾 Sayımı Kaydet</Button>
                    <Button variant="primary" onClick={adjust} loading={adjusting}>✓ Onayla & Fark Fişi Üret</Button>
                  </>
                )}
                {mode === 'view' && (
                  <Button variant="ghost" onClick={() => setShowModal(false)}>Kapat</Button>
                )}
              </div>
            </>
          }
        >
          {msg && (
            <div style={{
              padding: '10px 14px', marginBottom: 12, borderRadius: 8, fontSize: '0.85rem',
              background: msg.type === 'error' ? 'rgba(220,38,38,0.1)' : 'rgba(22,163,74,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : 'var(--success)',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(220,38,38,0.3)' : 'rgba(22,163,74,0.3)'),
            }}>{msg.text}</div>
          )}

          {mode !== 'add' && selected && (
            <CardHero
              icon="📋"
              code={selected.count_no}
              title="Stok Sayım"
              subtitle={`${fmtDate(selected.count_date)} · ${selected.warehouse_name || 'Tüm depolar'}`}
              statusBadge={<CardStatusBadge preset={selected.status === 'adjusted' ? 'approved' : selected.status === 'cancelled' ? 'cancelled' : 'pending'} />}
              kpis={[
                { label: 'Toplam Satır', value: lines.length, color: 'info' },
                { label: 'Fark Satır', value: lines.filter(l => parseFloat(l.variance || 0) !== 0).length, color: 'warning' },
                ...(selected.surplus_voucher_no ? [{ label: 'Fazlalık Fişi', value: selected.surplus_voucher_no, color: 'success' }] : []),
                ...(selected.deficit_voucher_no ? [{ label: 'Eksiklik Fişi', value: selected.deficit_voucher_no, color: 'danger' }] : []),
              ]}
            />
          )}

          {mode === 'add' ? (
            <AddScopeForm scope={scope} setScope={setScope} lookups={lookups} />
          ) : (
            <CountContent
              selected={selected}
              lines={lines}
              updateLineQty={updateLineQty}
              tab={tab} setTab={setTab}
              readOnly={selected?.status === 'adjusted' || selected?.status === 'cancelled'}
              onNavigate={onNavigate}
              onCloseModal={() => setShowModal(false)}
            />
          )}
        </Modal>
      )}
    </div>
  );
}

function AddScopeForm({ scope, setScope, lookups }) {
  const s = (k, v) => setScope(prev => ({ ...prev, [k]: v }));
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{
        padding: '10px 14px', background: 'rgba(59,130,246,0.06)', borderRadius: 8,
        fontSize: '0.82rem', color: 'var(--text-secondary)', border: '1px solid rgba(59,130,246,0.2)',
      }}>
        💡 Sayım için kapsam belirleyin (depo + opsiyonel kategori/marka filtreleri). "Sayımı Başlat" butonu ile o kapsamdaki <strong>tüm stoklar</strong> sayım satırı olarak yüklenir; sayılan miktarları sayım modunda girersiniz.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <FormField label="Tarih *">
          <input type="date" value={scope.count_date} onChange={e => s('count_date', e.target.value)} />
        </FormField>
        <FormField label="Sayım Türü">
          <select value={scope.count_type} onChange={e => s('count_type', e.target.value)}>
            {COUNT_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </FormField>
        <FormField label="Depo *" hint="Tek depo seçin (zorunlu)">
          <SearchableSelect
            value={scope.warehouse_id} onChange={v => s('warehouse_id', v)}
            options={lookups.warehouses}
            getValue={w => w.id} getLabel={w => `${w.code} - ${w.name}`}
            placeholder="Depo seç..."
          />
        </FormField>
        <FormField label="Kategori (ops.)">
          <SearchableSelect
            value={scope.category_id} onChange={v => s('category_id', v)}
            options={lookups.categories}
            getValue={c => c.id} getLabel={c => c.name}
            placeholder="Tüm kategoriler" allowClear
          />
        </FormField>
        <FormField label="Marka (ops.)">
          <SearchableSelect
            value={scope.brand_id} onChange={v => s('brand_id', v)}
            options={lookups.brands}
            getValue={b => b.id} getLabel={b => b.name}
            placeholder="Tüm markalar" allowClear
          />
        </FormField>
        <FormField label="Personel (ops.)">
          <SearchableSelect
            value={scope.personnel_id} onChange={v => s('personnel_id', v)}
            options={lookups.personnel || []}
            getValue={p => p.id} getLabel={p => (p.code ? p.code + ' - ' : '') + p.full_name}
            placeholder="Personel seç..." allowClear
          />
        </FormField>
        <FormField label="Özel Kod 1">
          <SearchableSelect
            value={scope.special_code1} onChange={v => s('special_code1', v)}
            options={lookups.specialCodes1 || []}
            getValue={x => x.code} getLabel={x => x.code + ' - ' + x.name}
            placeholder="Özel Kod 1..." allowClear
          />
        </FormField>
        <FormField label="Özel Kod 2">
          <SearchableSelect
            value={scope.special_code2} onChange={v => s('special_code2', v)}
            options={lookups.specialCodes2 || []}
            getValue={x => x.code} getLabel={x => x.code + ' - ' + x.name}
            placeholder="Özel Kod 2..." allowClear
          />
        </FormField>
      </div>
      <FormField label="Açıklama">
        <input value={scope.description} onChange={e => s('description', e.target.value)} placeholder="Yıl sonu sayımı / Aralık kontrolü..." />
      </FormField>
    </div>
  );
}

function CountContent({ selected, lines, updateLineQty, tab, setTab, readOnly, onNavigate, onCloseModal }) {
  const varianceLines = lines.filter(l => parseFloat(l.variance || 0) !== 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-secondary)', marginBottom: 16, marginTop: 12 }}>
        <TabBtn active={tab === 'count'} onClick={() => setTab('count')}>📝 Sayım ({lines.length})</TabBtn>
        <TabBtn active={tab === 'review'} onClick={() => setTab('review')}>⚖ Mutabakat ({varianceLines.length})</TabBtn>
      </div>

      {tab === 'count' ? (
        <CountTable lines={lines} updateLineQty={updateLineQty} readOnly={readOnly} />
      ) : (
        <ReviewTable lines={varianceLines} updateLineQty={updateLineQty} readOnly={readOnly}
          selected={selected} onNavigate={onNavigate} onCloseModal={onCloseModal} />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer',
      borderBottom: '2px solid ' + (active ? 'var(--accent-primary)' : 'transparent'),
      color: active ? 'var(--accent-primary)' : 'var(--text-secondary)',
      fontWeight: active ? 600 : 400, fontSize: '0.88rem',
      marginBottom: -1,
    }}>{children}</button>
  );
}

function CountTable({ lines, updateLineQty, readOnly }) {
  const [barcodeInput, setBarcodeInput] = React.useState('');
  const [scanMsg, setScanMsg] = React.useState(null);
  const [highlightId, setHighlightId] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const barcodeRef = React.useRef(null);
  const rowRefs = React.useRef({});

  const handleBarcodeScan = () => {
    const code = (barcodeInput || '').trim();
    if (!code) return;
    const match = lines.find(l => (l.stock_barcode || '').toLowerCase() === code.toLowerCase()
                              || (l.stock_code || '').toLowerCase() === code.toLowerCase());
    if (!match) {
      setScanMsg({ type: 'error', text: `❌ Barkod bulunamadı: ${code}` });
      setTimeout(() => setScanMsg(null), 2500);
      return;
    }
    const newQty = (parseFloat(match.counted_qty) || 0) + 1;
    updateLineQty(match.id, 'counted_qty', newQty);
    setHighlightId(match.id);
    setScanMsg({ type: 'success', text: `✓ ${match.stock_code} — ${match.stock_name} → ${newQty}` });
    setTimeout(() => setHighlightId(null), 1000);
    setTimeout(() => setScanMsg(null), 1500);
    setBarcodeInput('');
    // Satıra scroll
    setTimeout(() => {
      const el = rowRefs.current[match.id];
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (barcodeRef.current) barcodeRef.current.focus();
    }, 50);
  };

  // Arama
  const visible = search
    ? lines.filter(l => {
        const q = search.toLowerCase();
        return (l.stock_code || '').toLowerCase().includes(q)
            || (l.stock_name || '').toLowerCase().includes(q)
            || (l.stock_barcode || '').toLowerCase().includes(q);
      })
    : lines;

  if (lines.length === 0) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-tertiary)' }}>Satır yok</div>;

  return (
    <div>
      {/* Barkod toolbar */}
      {!readOnly && (
        <div style={{
          display: 'flex', gap: 8, padding: '10px 12px', marginBottom: 8,
          background: 'var(--bg-secondary)', borderRadius: 8, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <div style={{ position: 'relative', display: 'flex', gap: 4, alignItems: 'stretch' }}>
            <div style={{ position: 'relative' }}>
              <input
                ref={barcodeRef}
                value={barcodeInput}
                onChange={e => setBarcodeInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleBarcodeScan(); } }}
                placeholder="Barkod okut / Enter → +1 ekler"
                autoFocus
                style={{
                  width: 240, height: 34, padding: '0 10px 0 36px', borderRadius: 8,
                  border: '1px solid var(--border-input)',
                  background: 'var(--bg-input)', color: 'var(--text-primary)',
                  fontSize: '0.85rem', fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.4, pointerEvents: 'none' }}>
                <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="7" y1="8" x2="7" y2="16"/><line x1="11" y1="8" x2="11" y2="16"/><line x1="15" y1="8" x2="15" y2="16"/>
              </svg>
            </div>
            <button type="button" onClick={handleBarcodeScan} title="Okut / +1 ekle"
              style={{
                height: 34, width: 34, padding: 0,
                background: 'var(--bg-input)', border: '1px solid var(--border-input)',
                borderRadius: 8, cursor: 'pointer', fontSize: '0.95rem', lineHeight: 1, boxSizing: 'border-box',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>📷</button>
          </div>

          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Stok ara (kod/ad/barkod)..."
            style={{ flex: '0 0 auto', width: 200, height: 34, padding: '0 10px', borderRadius: 8, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '0.82rem', boxSizing: 'border-box' }} />

          {scanMsg && (
            <div style={{
              padding: '6px 12px', borderRadius: 6, fontSize: '0.82rem', fontWeight: 600,
              background: scanMsg.type === 'error' ? 'rgba(220,38,38,0.12)' : 'rgba(22,163,74,0.12)',
              color: scanMsg.type === 'error' ? 'var(--danger)' : 'var(--success)',
              border: '1px solid ' + (scanMsg.type === 'error' ? 'rgba(220,38,38,0.3)' : 'rgba(22,163,74,0.3)'),
            }}>{scanMsg.text}</div>
          )}

          <div style={{ flex: 1 }} />
          <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
            💡 Her okutmada sayılan miktar +1 artar
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto', maxHeight: 460, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-primary)', zIndex: 1 }}>
            <tr style={{ borderBottom: '1px solid var(--border-secondary)', color: 'var(--text-tertiary)', fontSize: '0.72rem', textTransform: 'uppercase' }}>
              <th style={{ width: 40, padding: 6 }}>#</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', width: 140 }}>Stok Kodu</th>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Stok Adı</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', width: 130 }}>Barkod</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', width: 110 }}>Sistem Mevcut</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', width: 130 }}>Sayılan</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', width: 100 }}>Fark</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}>Aramayla eşleşen satır yok</td></tr>
            )}
            {visible.map(l => {
              const variance = parseFloat(l.variance) || 0;
              const isHighlight = highlightId === l.id;
              return (
                <tr key={l.id}
                  ref={el => { if (el) rowRefs.current[l.id] = el; }}
                  style={{
                    borderBottom: '1px solid var(--border-secondary)',
                    background: isHighlight ? 'rgba(22,163,74,0.15)' : 'transparent',
                    transition: 'background 400ms',
                  }}>
                  <td style={{ padding: '6px 6px', color: 'var(--text-tertiary)', textAlign: 'center' }}>{l.line_no}</td>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '0.78rem' }}>{l.stock_code}</td>
                  <td style={{ padding: '6px 8px' }}>{l.stock_name}</td>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{l.stock_barcode || '-'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--text-tertiary)' }}>{fmt(l.expected_qty)}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <input type="number" step="0.001" value={l.counted_qty}
                      onChange={e => updateLineQty(l.id, 'counted_qty', e.target.value)}
                      disabled={readOnly}
                      style={{ width: '100%', padding: '5px 8px', textAlign: 'right', fontFamily: 'monospace',
                               background: 'var(--bg-input)', border: '1px solid var(--border-input)', borderRadius: 4 }} />
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600,
                                color: variance > 0 ? 'var(--success)' : variance < 0 ? 'var(--danger)' : 'var(--text-tertiary)' }}>
                    {variance > 0 ? '+' : ''}{fmt(variance)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReviewTable({ lines, updateLineQty, readOnly, selected, onNavigate, onCloseModal }) {
  const [filter, setFilter] = React.useState('all'); // 'all' | 'surplus' | 'deficit' | 'no_reason'
  const [sortMode, setSortMode] = React.useState('abs_desc'); // 'abs_desc' | 'line_asc' | 'stock_asc'
  const [bulkReason, setBulkReason] = React.useState('');
  const [bulkScope, setBulkScope] = React.useState('empty'); // 'empty' | 'all_visible'
  const [picked, setPicked] = React.useState(new Set());
  const [search, setSearch] = React.useState('');

  if (lines.length === 0) return (
    <div style={{ padding: 32, textAlign: 'center', color: 'var(--success)' }}>
      ✓ Hiç fark yok — sistem mevcudu ile sayılan miktar eşleşiyor
    </div>
  );

  const SURPLUS_REASONS = ['Sistem Hatası', 'Kayda Geçmemiş Giriş', 'Diğer'];
  const DEFICIT_REASONS = ['Sayım Hatası', 'Çalıntı/Kayıp', 'Fire/Bozulma', 'Sebep Bilinmiyor', 'Diğer'];

  // Filtreleme
  let visible = lines;
  if (filter === 'surplus')   visible = visible.filter(l => parseFloat(l.variance) > 0);
  if (filter === 'deficit')   visible = visible.filter(l => parseFloat(l.variance) < 0);
  if (filter === 'no_reason') visible = visible.filter(l => !l.reason_code);
  if (search) {
    const q = search.toLowerCase();
    visible = visible.filter(l => (l.stock_code || '').toLowerCase().includes(q) || (l.stock_name || '').toLowerCase().includes(q));
  }

  // Sıralama
  visible = [...visible];
  if (sortMode === 'abs_desc') visible.sort((a,b) => Math.abs(parseFloat(b.variance)||0) - Math.abs(parseFloat(a.variance)||0));
  else if (sortMode === 'line_asc') visible.sort((a,b) => (a.line_no||0) - (b.line_no||0));
  else if (sortMode === 'stock_asc') visible.sort((a,b) => (a.stock_code||'').localeCompare(b.stock_code||''));

  const surplusCount = lines.filter(l => parseFloat(l.variance) > 0).length;
  const deficitCount = lines.filter(l => parseFloat(l.variance) < 0).length;
  const noReasonCount = lines.filter(l => !l.reason_code).length;
  const totalSurplusQty = lines.filter(l => parseFloat(l.variance) > 0).reduce((s,l) => s + parseFloat(l.variance), 0);
  const totalDeficitQty = lines.filter(l => parseFloat(l.variance) < 0).reduce((s,l) => s + Math.abs(parseFloat(l.variance)), 0);
  // Maliyet etkisi (ağırlıklı ortalama)
  const totalSurplusValue = lines.filter(l => parseFloat(l.variance) > 0)
    .reduce((s,l) => s + parseFloat(l.variance) * (parseFloat(l.unit_cost) || 0), 0);
  const totalDeficitValue = lines.filter(l => parseFloat(l.variance) < 0)
    .reduce((s,l) => s + Math.abs(parseFloat(l.variance)) * (parseFloat(l.unit_cost) || 0), 0);
  const netEffect = totalSurplusValue - totalDeficitValue;

  const applyBulkReason = () => {
    if (!bulkReason) return;
    if (bulkScope === 'empty') {
      // Sadece sebebi boş olanlara uygula (görünür satırlar arasında)
      const visibleIds = new Set(visible.filter(l => !l.reason_code).map(l => l.id));
      visibleIds.forEach(id => updateLineQty(id, 'reason_code', bulkReason));
    } else {
      // Görünen tüm satırlara uygula
      visible.forEach(l => updateLineQty(l.id, 'reason_code', bulkReason));
    }
  };

  const applyToPicked = () => {
    if (!bulkReason || picked.size === 0) return;
    picked.forEach(id => updateLineQty(id, 'reason_code', bulkReason));
    setPicked(new Set());
  };

  const toggle = (id) => setPicked(prev => {
    const ns = new Set(prev);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    return ns;
  });

  const toggleAllVisible = () => {
    if (picked.size > 0) setPicked(new Set());
    else setPicked(new Set(visible.map(l => l.id)));
  };

  return (
    <div>
      {selected?.status === 'adjusted' && (selected.surplus_voucher_no || selected.deficit_voucher_no) && (
        <div style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(22,163,74,0.08)', borderRadius: 8, border: '1px solid rgba(22,163,74,0.3)', fontSize: '0.85rem' }}>
          <strong>✓ Sayım onaylandı.</strong> Üretilen fişler:
          <div style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {selected.surplus_voucher_no && (
              <button onClick={() => { onCloseModal(); onNavigate?.('stock-count-surplus', selected.surplus_voucher_id); }}
                style={{ background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.4)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: 'var(--success)', fontWeight: 600 }}>
                ➕ Sayım Fazlası: {selected.surplus_voucher_no}
              </button>
            )}
            {selected.deficit_voucher_no && (
              <button onClick={() => { onCloseModal(); onNavigate?.('stock-count-deficit', selected.deficit_voucher_id); }}
                style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.4)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', color: 'var(--danger)', fontWeight: 600 }}>
                ➖ Sayım Eksiği: {selected.deficit_voucher_no}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Özet ve hint */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: '0.82rem', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span><strong style={{ color: 'var(--success)' }}>+{surplusCount}</strong> fazla satır <span style={{ color: 'var(--text-tertiary)' }}>(+{fmt(totalSurplusQty)})</span></span>
          <span><strong style={{ color: 'var(--danger)' }}>−{deficitCount}</strong> eksik satır <span style={{ color: 'var(--text-tertiary)' }}>(−{fmt(totalDeficitQty)})</span></span>
          {!readOnly && noReasonCount > 0 && (
            <span style={{ color: 'var(--warning)' }}>{noReasonCount} satır sebepsiz</span>
          )}
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
          💡 Sebep opsiyoneldir — boş bıraksanız da onaylayabilirsiniz
        </div>
      </div>

      {/* Maliyet etkisi paneli */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12,
        padding: '12px 16px', marginBottom: 10,
        background: 'var(--bg-secondary)', borderRadius: 10, border: '1px solid var(--border-secondary)',
      }}>
        <div>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Toplam Fazlalık Değeri</div>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, fontFamily: 'monospace', color: 'var(--success)' }}>+{fmt(totalSurplusValue)} ₺</div>
        </div>
        <div>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Toplam Eksiklik Değeri</div>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, fontFamily: 'monospace', color: 'var(--danger)' }}>−{fmt(totalDeficitValue)} ₺</div>
        </div>
        <div style={{ borderLeft: '2px solid var(--border-secondary)', paddingLeft: 16 }}>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Net Etki</div>
          <div style={{ fontSize: '1.2rem', fontWeight: 800, fontFamily: 'monospace',
            color: netEffect >= 0 ? 'var(--success)' : 'var(--danger)' }}>
            {netEffect >= 0 ? '+' : ''}{fmt(netEffect)} ₺
          </div>
        </div>
        <div style={{ alignSelf: 'center' }}>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
            Maliyet hesabı: <strong>Ağırlıklı Ortalama</strong>
            <br/>(geçmiş alış hareketlerinden hesaplanır)
          </div>
        </div>
      </div>

      {/* Toolbar — filtre + sıralama + toplu işlem */}
      {!readOnly && (
        <div style={{
          padding: '10px 12px', marginBottom: 8, background: 'var(--bg-secondary)', borderRadius: 8,
          display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
        }}>
          {/* Filtre butonları */}
          <div style={{ display: 'flex', gap: 4, border: '1px solid var(--border-input)', borderRadius: 6, padding: 2 }}>
            <FilterBtn active={filter==='all'} onClick={() => setFilter('all')}>Tümü ({lines.length})</FilterBtn>
            <FilterBtn active={filter==='surplus'} onClick={() => setFilter('surplus')} color="#16a34a">+ Fazlalar ({surplusCount})</FilterBtn>
            <FilterBtn active={filter==='deficit'} onClick={() => setFilter('deficit')} color="#dc2626">− Eksikler ({deficitCount})</FilterBtn>
            <FilterBtn active={filter==='no_reason'} onClick={() => setFilter('no_reason')} color="#f59e0b">⚠ Sebepsiz ({noReasonCount})</FilterBtn>
          </div>
          {/* Arama */}
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Stok ara..."
            style={{ flex: '0 0 auto', width: 160, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '0.82rem' }} />
          {/* Sıralama */}
          <select value={sortMode} onChange={e => setSortMode(e.target.value)}
            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '0.82rem' }}>
            <option value="abs_desc">Sırala: En büyük fark</option>
            <option value="line_asc">Sırala: Satır no</option>
            <option value="stock_asc">Sırala: Stok kodu</option>
          </select>

          <div style={{ flex: 1 }} />

          {/* Toplu sebep ata */}
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>Toplu sebep:</span>
            <select value={bulkReason} onChange={e => setBulkReason(e.target.value)}
              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '0.82rem', minWidth: 160 }}>
              <option value="">— Sebep seç —</option>
              <optgroup label="Fazlalık için">
                {SURPLUS_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </optgroup>
              <optgroup label="Eksiklik için">
                {DEFICIT_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </optgroup>
            </select>
            <select value={bulkScope} onChange={e => setBulkScope(e.target.value)}
              style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-input)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '0.82rem' }}>
              <option value="empty">Sadece boş olanlara</option>
              <option value="all_visible">Görünen tümüne</option>
            </select>
            <button onClick={applyBulkReason} disabled={!bulkReason}
              style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: bulkReason ? 'var(--accent-primary)' : 'var(--bg-input)', color: 'white', cursor: bulkReason ? 'pointer' : 'not-allowed', fontSize: '0.82rem', fontWeight: 600 }}>
              Uygula
            </button>
            {picked.size > 0 && (
              <button onClick={applyToPicked} disabled={!bulkReason}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--accent-primary)', background: 'transparent', color: 'var(--accent-primary)', cursor: bulkReason ? 'pointer' : 'not-allowed', fontSize: '0.82rem', fontWeight: 600 }}>
                Sadece seçili {picked.size}'e
              </button>
            )}
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto', maxHeight: 460, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-primary)', zIndex: 1 }}>
            <tr style={{ borderBottom: '1px solid var(--border-secondary)', color: 'var(--text-tertiary)', fontSize: '0.72rem', textTransform: 'uppercase' }}>
              {!readOnly && (
                <th style={{ width: 32, padding: 6, textAlign: 'center' }}>
                  <input type="checkbox" checked={picked.size > 0 && picked.size === visible.length} onChange={toggleAllVisible} />
                </th>
              )}
              <th style={{ width: 40, padding: 6 }}>#</th>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Stok</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', width: 90 }}>Sistem</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', width: 90 }}>Sayılan</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', width: 90 }}>Fark</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', width: 110 }}>Fark Değeri</th>
              <th style={{ textAlign: 'left', padding: '6px 8px', width: 170 }}>Sebep (ops.)</th>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Not</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={readOnly ? 8 : 9} style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}>
                Bu filtreyle satır yok
              </td></tr>
            )}
            {visible.map(l => {
              const v = parseFloat(l.variance) || 0;
              const reasons = v > 0 ? SURPLUS_REASONS : DEFICIT_REASONS;
              const isPicked = picked.has(l.id);
              return (
                <tr key={l.id} style={{
                  borderBottom: '1px solid var(--border-secondary)',
                  background: isPicked ? 'rgba(59,130,246,0.06)' : 'transparent',
                }}>
                  {!readOnly && (
                    <td style={{ padding: '6px 6px', textAlign: 'center' }}>
                      <input type="checkbox" checked={isPicked} onChange={() => toggle(l.id)} />
                    </td>
                  )}
                  <td style={{ padding: '6px 6px', color: 'var(--text-tertiary)', textAlign: 'center' }}>{l.line_no}</td>
                  <td style={{ padding: '6px 8px' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.74rem', color: 'var(--text-tertiary)' }}>{l.stock_code}</div>
                    <div>{l.stock_name}</div>
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', color: 'var(--text-tertiary)' }}>{fmt(l.expected_qty)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmt(l.counted_qty)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: v > 0 ? 'var(--success)' : 'var(--danger)' }}>
                    {v > 0 ? '+' : ''}{fmt(v)}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600,
                                color: v > 0 ? 'var(--success)' : 'var(--danger)', whiteSpace: 'nowrap' }}>
                    {(() => {
                      const cost = parseFloat(l.unit_cost) || 0;
                      const value = Math.abs(v) * cost;
                      if (cost === 0) return <span style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }} title="Maliyet bilgisi yok (alış hareketi olmamış)">—</span>;
                      return (v > 0 ? '+' : '−') + fmt(value);
                    })()}
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <select value={l.reason_code || ''} onChange={e => updateLineQty(l.id, 'reason_code', e.target.value)}
                      disabled={readOnly}
                      style={{ width: '100%', padding: '4px 6px', background: 'var(--bg-input)', border: '1px solid ' + (l.reason_code ? 'var(--border-input)' : 'rgba(245,158,11,0.4)'), borderRadius: 4, fontSize: '0.78rem' }}>
                      <option value="">— Sebep —</option>
                      {reasons.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '6px 8px' }}>
                    <input value={l.line_notes || ''} onChange={e => updateLineQty(l.id, 'line_notes', e.target.value)}
                      disabled={readOnly} placeholder="Not..."
                      style={{ width: '100%', padding: '4px 6px', background: 'var(--bg-input)', border: '1px solid var(--border-input)', borderRadius: 4, fontSize: '0.78rem' }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterBtn({ active, onClick, color, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '5px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
      background: active ? (color || 'var(--accent-primary)') : 'transparent',
      color: active ? 'white' : (color || 'var(--text-secondary)'),
      fontSize: '0.78rem', fontWeight: active ? 600 : 500,
      transition: 'all 100ms',
    }}>{children}</button>
  );
}
